import axios from "axios";

import { CHECKLIST_API_BASE_URL } from "../config";
import { getAdminToken } from "./session";
import { errorMessage } from "./index";

const saveAs = require("file-saver");

// Confirmed against the live Swagger UI at
// https://operations-api-stag.rapsap.com/rapsap-operations/docs/ — its
// `servers[0].url` is "/rapsap-operations/api/v1", not just
// "/rapsap-operations" (that extra /api/v1 segment isn't mentioned in the
// original API reference doc). Every 404 "Route not found" seen while this
// was wrong went away once this was added — confirmed via curl, which
// switched from 404 to 401 "Missing bearer token" on the same routes.
const CHECKLIST_API_ROOT = `${CHECKLIST_API_BASE_URL}/rapsap-operations/api/v1`;

/**
 * API helper for the Staff Ops / Checklist backend (`${CHECKLIST_API_ROOT}/...`).
 * Separate from getApi/fetchApi in src/helpers/index.js because this is a
 * different backend with, per its own doc, real HTTP status codes
 * throughout (no "always-200, check `success`" special case like the main
 * dashboard's login). It accepts the same login token as the main API.
 *
 * Returns the parsed response body's `data` on success, or `null` on any
 * failure (missing token, network error, non-2xx status) — callers should
 * treat `null` as "couldn't load, already reported to the user" rather
 * than distinguishing failure reasons.
 */
async function checklistRequest(method, path, { params, data } = {}) {
  const token = getAdminToken();
  if (!token) {
    errorMessage("Not logged in.");
    return null;
  }

  try {
    const response = await axios({
      method,
      url: `${CHECKLIST_API_ROOT}/${path.replace(/^\//, "")}`,
      params,
      data,
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data?.data ?? null;
  } catch (error) {
    errorMessage(error?.response?.data?.message || "Checklist API request failed.");
    return null;
  }
}

// GET /me — current profile (name, access level, store id, roles). Used to
// get a human-readable label for the logged-in Store Manager's header,
// since none of the /stores/{storeId}/checklist/* responses include the
// store's display name.
export function getMe() {
  return checklistRequest("get", "me");
}

// GET /admin/matrix?date=YYYY-MM-DD — full item x store grid for one day.
// `date` is optional; the backend defaults to today (IST) when omitted.
export function getAdminMatrix(date) {
  return checklistRequest("get", "admin/matrix", { params: date ? { date } : undefined });
}

// GET /admin/stores — all stores with status + current rating.
export function getAdminStores() {
  return checklistRequest("get", "admin/stores");
}

// GET /admin/roles — checklist roles, [{ id, name, isActive }].
export function getAdminRoles() {
  return checklistRequest("get", "admin/roles");
}

// POST /admin/stores/{storeId}/rating — record the store's 1-5 star daily
// rating. Calling again for the same store+day updates the existing rating
// rather than duplicating it (per the doc). No date param exists on this
// endpoint — the backend assigns `ratingDate` itself (presumably today),
// so this always rates "today," not whatever date the matrix is showing.
export function rateStore(storeId, { score, remarks }) {
  return checklistRequest("post", `admin/stores/${storeId}/rating`, { data: { score, remarks } });
}

// GET /admin/stores/{storeId}/monthly-score?year=&month= — averaged daily
// ratings for a month. `averageScore` is null / `ratingCount` is 0 when no
// ratings exist yet — not an error.
export function getMonthlyScore(storeId, year, month) {
  return checklistRequest("get", `admin/stores/${storeId}/monthly-score`, { params: { year, month } });
}

// GET /admin/export?format=pdf|excel&storeId?&date? — binary file download,
// not the usual JSON envelope, so this bypasses checklistRequest entirely
// and triggers a browser download directly (via file-saver, same library
// already used for CSV exports elsewhere in this app — see
// src/screens/Orders/index.js). Returns true/false rather than data.
export async function exportChecklist({ format = "excel", storeId, date } = {}) {
  const token = getAdminToken();
  if (!token) {
    errorMessage("Not logged in.");
    return false;
  }

  try {
    const response = await axios.get(`${CHECKLIST_API_ROOT}/admin/export`, {
      params: { format, storeId, date },
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    });
    const extension = format === "pdf" ? "pdf" : "xlsx";
    const filename = `checklist-export${date ? `-${date}` : ""}.${extension}`;
    saveAs.saveAs(response.data, filename);
    return true;
  } catch (error) {
    errorMessage("Export failed.");
    return false;
  }
}

// GET /stores/{storeId}/checklist/today — every worker's submission status
// for the store, plus section-level completed/total/status.
export function getStoreToday(storeId) {
  return checklistRequest("get", `stores/${storeId}/checklist/today`);
}

// GET /stores/{storeId}/checklist/team-progress — progress by role; the
// doc calls this "the desktop dashboard's primary view" for Store Managers.
export function getStoreTeamProgress(storeId) {
  return checklistRequest("get", `stores/${storeId}/checklist/team-progress`);
}

// GET /stores/{storeId}/checklist/roles/{roleId} — item-level detail for
// one role (e.g. "Housekeeping checklist"), drilling down from team-progress.
export function getRoleDetail(storeId, roleId) {
  return checklistRequest("get", `stores/${storeId}/checklist/roles/${roleId}`);
}

// POST /stores/{storeId}/checklist/submit-to-hq — finalize the day's
// reviewed report. `managerComments` is optional.
export function submitToHq(storeId, managerComments) {
  return checklistRequest("post", `stores/${storeId}/checklist/submit-to-hq`, {
    data: { managerComments },
  });
}

// GET /checklist/items/{id} — full detail on one submission (photo,
// remarks, notes thread). `submissionId`, NOT the checklist item's own id
// — the doc is explicit that these are different ids that happen to look
// similar (submissionId comes from a matrix cell's `submissionId` field).
export function getSubmissionDetail(submissionId) {
  return checklistRequest("get", `checklist/items/${submissionId}`);
}

// POST /admin/stores/{storeId}/items/{itemId}/notes — reply to a flagged
// submission. Doc's own "naming gotcha": despite the `{itemId}` name in
// the path, this must be the submission id too, not the checklist item id.
// multipart/form-data; `photo` is required on every note.
export function addSubmissionNote(storeId, submissionId, { note, photo }) {
  const formData = new FormData();
  formData.append("note", note);
  formData.append("photo", photo);
  return checklistRequest("post", `admin/stores/${storeId}/items/${submissionId}/notes`, { data: formData });
}
